/**
 * Resolve hero images for purchased / pending customer subscriptions.
 * Merged subscription APIs often omit thumbnail; fall back to catalog plan lookup (SubscriptionList parity).
 */
import { pickCmsPageImageUri, extractFirstImageSrcFromHtml } from '../../../shared/utils/resolveCmsImageUri';
import { resolveImageUri } from '../../../shared/utils/resolveImageUri';
import { resolveMediaUri } from '../../../shared/utils/resolveMediaUri';
import type { SubscriptionData } from './types';

function pick_string(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function coerce_day_off(value: unknown): number | number[] {
  if (value == null) {
    return 0;
  }
  if (Array.isArray(value)) {
    return value.map(v => Number(v)).filter(n => Number.isFinite(n));
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalize_plan_title(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Non-empty thumbnail/attachment on a catalog plan-like object (SubscriptionListScreen parity). */
export function planImageFromCatalogPlanLike(obj: unknown): string | null {
  if (obj == null || typeof obj !== 'object') {
    return resolveMediaUri(obj);
  }
  const o = obj as Record<string, unknown>;
  for (const key of ['thumbnail', 'attachment', 'attachments', 'image', 'banner', 'cover_image', 'featured_image']) {
    const resolved = resolveMediaUri(o[key]);
    if (resolved) {
      return resolved;
    }
  }
  const html_fields = [o.description, o.a_description, o.content];
  for (const html of html_fields) {
    if (typeof html === 'string' && html.trim()) {
      const from_html = extractFirstImageSrcFromHtml(html);
      if (from_html) {
        const resolved = resolveImageUri(from_html);
        if (resolved) {
          return resolved;
        }
      }
    }
  }
  return pickCmsPageImageUri(o);
}

function resolve_merged_subscription_image_fields(sub: SubscriptionData): string | null {
  const sub_record = sub as SubscriptionData & Record<string, unknown>;
  const detail = sub.detail ?? {};
  const detail_record = detail as Record<string, unknown>;

  const picked =
    pickCmsPageImageUri(sub_record) ??
    pickCmsPageImageUri(detail_record) ??
    (sub.subscription_detail != null && typeof sub.subscription_detail === 'object'
      ? pickCmsPageImageUri(sub.subscription_detail as Record<string, unknown>)
      : null);
  if (picked) {
    return picked;
  }

  const candidates = [
    sub.image,
    sub.thumbnail,
    sub.attachments,
    sub_record.attachments,
    sub_record.attachment,
    detail.image,
    detail.thumbnail,
    detail_record.attachment,
    detail_record.attachments,
  ];
  for (const raw of candidates) {
    const resolved = resolveMediaUri(raw);
    if (resolved) {
      return resolved;
    }
    if (typeof raw === 'string' && raw.trim()) {
      const extracted = extractFirstImageSrcFromHtml(raw);
      if (extracted) {
        const from_html = resolveImageUri(extracted);
        if (from_html) {
          return from_html;
        }
      }
    }
  }
  return null;
}

function* walk_catalog_plans(categories: unknown[]): Generator<Record<string, unknown>> {
  for (const cat of categories) {
    if (cat == null || typeof cat !== 'object') {
      continue;
    }
    const c = cat as Record<string, unknown>;

    for (const part of (c.particulars as unknown[]) ?? []) {
      if (part != null && typeof part === 'object') {
        yield part as Record<string, unknown>;
      }
    }

    for (const sub of (c.subscriptions as unknown[]) ?? []) {
      if (sub == null || typeof sub !== 'object') {
        continue;
      }
      for (const plan of ((sub as Record<string, unknown>).type_data as unknown[]) ?? []) {
        if (plan != null && typeof plan === 'object') {
          yield plan as Record<string, unknown>;
        }
      }
    }

    for (const day of (c.days as unknown[]) ?? []) {
      if (day == null || typeof day !== 'object') {
        continue;
      }
      const day_record = day as Record<string, unknown>;
      for (const tagged of (day_record.tagged_plans as unknown[]) ?? []) {
        if (tagged == null || typeof tagged !== 'object') {
          continue;
        }
        for (const plan of ((tagged as Record<string, unknown>).plans as unknown[]) ?? []) {
          if (plan != null && typeof plan === 'object') {
            yield plan as Record<string, unknown>;
          }
        }
      }
      for (const plan of (day_record.subscriptions as unknown[]) ?? []) {
        if (plan != null && typeof plan === 'object') {
          yield plan as Record<string, unknown>;
        }
      }
    }
  }
}

function catalog_plan_day(plan: Record<string, unknown>): number {
  return Number(plan.day ?? plan.days ?? plan.type_key ?? plan.duration ?? 0);
}

function extract_catalog_plan_id_candidates(sub: SubscriptionData): Set<number> {
  const sub_record = sub as SubscriptionData & Record<string, unknown>;
  const detail = sub.detail ?? {};
  const detail_record = detail as Record<string, unknown>;
  const sd = sub.subscription_detail;
  const sd_record = sd != null && typeof sd === 'object' ? (sd as Record<string, unknown>) : null;

  const ids = new Set<number>();
  for (const source of [sub_record, detail_record, sd_record]) {
    if (source == null) {
      continue;
    }
    for (const key of ['subscription_id', 'plan_id', 'catalog_subscription_id'] as const) {
      const n = Number(source[key]);
      if (Number.isFinite(n) && n > 0) {
        ids.add(Math.floor(n));
      }
    }
    if (source === sd_record) {
      const catalog_id = Number(source.id);
      if (Number.isFinite(catalog_id) && catalog_id > 0) {
        ids.add(Math.floor(catalog_id));
      }
    }
  }
  return ids;
}

function extract_title_candidates(sub: SubscriptionData): string[] {
  const detail = sub.detail ?? {};
  const detail_record = detail as Record<string, unknown>;
  const sub_record = sub as SubscriptionData & Record<string, unknown>;
  const titles = [
    detail.title,
    detail_record.a_title,
    detail_record.arabic_title,
    detail_record.name,
    sub.title,
    sub_record.subscription_name,
    sub.description,
  ];
  const out: string[] = [];
  for (const t of titles) {
    const n = normalize_plan_title(typeof t === 'string' ? t : null);
    if (n && !out.includes(n)) {
      out.push(n);
    }
  }
  return out;
}

function plan_title_candidates(plan: Record<string, unknown>): string[] {
  const titles = [plan.title, plan.arabic_title, plan.name, plan.a_title];
  const out: string[] = [];
  for (const t of titles) {
    const n = normalize_plan_title(typeof t === 'string' ? t : null);
    if (n && !out.includes(n)) {
      out.push(n);
    }
  }
  return out;
}

/** Find the catalog plan row that corresponds to a customer subscription (id, slug, then title + days). */
export function findCatalogPlanForSubscription(
  sub: SubscriptionData,
  categories: unknown[],
): Record<string, unknown> | null {
  if (!Array.isArray(categories) || categories.length === 0) {
    return null;
  }

  const id_candidates = extract_catalog_plan_id_candidates(sub);
  const slug =
    pick_string((sub as SubscriptionData & { slug?: string }).slug) ??
    pick_string(sub.identifier) ??
    (sub.subscription_detail != null && typeof sub.subscription_detail === 'object'
      ? pick_string((sub.subscription_detail as Record<string, unknown>).slug)
      : null);

  const title_candidates = extract_title_candidates(sub);
  const day = Number(sub.detail?.day ?? sub.detail?.days ?? 0) || null;

  const plans = [...walk_catalog_plans(categories)];

  if (id_candidates.size > 0) {
    for (const plan of plans) {
      const plan_id = Number(plan.id ?? plan.subscription_id);
      if (Number.isFinite(plan_id) && id_candidates.has(Math.floor(plan_id))) {
        return plan;
      }
    }
  }

  if (slug) {
    const slug_l = slug.toLowerCase();
    for (const plan of plans) {
      const plan_slug = pick_string(plan.slug)?.toLowerCase();
      if (plan_slug && plan_slug === slug_l) {
        return plan;
      }
    }
  }

  for (const plan of plans) {
    const plan_titles = plan_title_candidates(plan);
    const plan_day = catalog_plan_day(plan);
    for (const title of title_candidates) {
      if (plan_titles.includes(title)) {
        if (day == null || plan_day === day || plan_day === 0) {
          return plan;
        }
      }
    }
  }

  if (day != null) {
    for (const plan of plans) {
      const plan_titles = plan_title_candidates(plan);
      const plan_day = catalog_plan_day(plan);
      if (plan_day !== day) {
        continue;
      }
      for (const title of title_candidates) {
        for (const plan_title of plan_titles) {
          if (plan_title.includes(title) || title.includes(plan_title)) {
            return plan;
          }
        }
      }
    }
  }

  return null;
}

/** Full image URI: merged subscription fields, then catalog plan thumbnail/attachment. */
export function resolveSubscriptionDisplayImageUri(
  sub: SubscriptionData,
  catalog_categories?: unknown[] | null,
): string | null {
  const from_merged = resolve_merged_subscription_image_fields(sub);
  if (from_merged) {
    return from_merged;
  }
  if (!catalog_categories?.length) {
    return null;
  }
  const plan = findCatalogPlanForSubscription(sub, catalog_categories);
  if (!plan) {
    return null;
  }
  return planImageFromCatalogPlanLike(plan);
}

/** Map a pending checkout row from `customer_subscriptions` list into `SubscriptionData` for My Subscriptions UI. */
export function mapPendingSubscriptionRawToData(raw: Record<string, unknown>, customer_id: number): SubscriptionData {
  const detail_raw = (raw.detail ?? {}) as Record<string, unknown>;
  const catalog_subscription_id = Number(raw.subscription_id);
  const mapped: SubscriptionData = {
    id: Number(raw.id),
    customer_id,
    start_date: pick_string(raw.start_date) ?? '',
    end_date: pick_string(raw.end_date) ?? '',
    status: pick_string(raw.status) ?? 'pending',
    freeze_dates: Array.isArray(raw.freeze_dates) ? (raw.freeze_dates as string[]) : [],
    pause_dates: Array.isArray(raw.paused_dates) ? (raw.paused_dates as string[]) : [],
    paused_dates: Array.isArray(raw.paused_dates) ? (raw.paused_dates as string[]) : [],
    skipped_dates: Array.isArray(raw.skipped_dates) ? (raw.skipped_dates as string[]) : [],
    double_box_dates: Array.isArray(raw.double_box_dates) ? (raw.double_box_dates as string[]) : [],
    used_freeze_count: Number(raw.used_freeze_count ?? 0),
    cost: Number(raw.final_cost ?? raw.cost ?? detail_raw.cost ?? 0),
    title: pick_string(detail_raw.title) ?? pick_string(raw.title) ?? undefined,
    description: pick_string(detail_raw.description) ?? pick_string(raw.description) ?? undefined,
    slug: pick_string(raw.slug) ?? undefined,
    identifier: pick_string(raw.identifier) ?? undefined,
    thumbnail: pick_string(raw.thumbnail) ?? undefined,
    image: pick_string(raw.image) ?? pick_string(raw.thumbnail) ?? undefined,
    attachments: pick_string(raw.attachments) ?? undefined,
    subscription_detail:
      raw.subscription_detail != null && typeof raw.subscription_detail === 'object'
        ? {
            ...(raw.subscription_detail as Record<string, unknown>),
            ...(Number.isFinite(catalog_subscription_id) && catalog_subscription_id > 0
              ? { subscription_id: catalog_subscription_id, id: catalog_subscription_id }
              : {}),
          }
        : Number.isFinite(catalog_subscription_id) && catalog_subscription_id > 0
          ? { subscription_id: catalog_subscription_id, id: catalog_subscription_id }
          : undefined,
    detail: {
      day: detail_raw.day != null ? Number(detail_raw.day) : undefined,
      days: detail_raw.days != null ? Number(detail_raw.days) : undefined,
      day_off: coerce_day_off(detail_raw.day_off),
      total_meals: Number(detail_raw.total_meals ?? 0),
      total_snacks: Number(detail_raw.total_snacks ?? 0),
      minimum_calories: Number(detail_raw.minimum_calories ?? 0),
      maximum_calories: Number(detail_raw.maximum_calories ?? 9999999),
      maximum_carbs: Number(detail_raw.maximum_carbs ?? 0),
      maximum_protein: Number(detail_raw.maximum_protein ?? 0),
      default_carbs: Number(detail_raw.default_carbs ?? 50),
      is_variable_carbs: Boolean(detail_raw.is_variable_carbs),
      force_fixed_menu: Boolean(detail_raw.force_fixed_menu),
      allowed_freeze_days: detail_raw.allowed_freeze_days != null ? Number(detail_raw.allowed_freeze_days) : undefined,
      selection_limit: (detail_raw.selection_limit as SubscriptionData['detail']['selection_limit']) ?? {},
      cost: detail_raw.cost != null ? Number(detail_raw.cost) : undefined,
      title: pick_string(detail_raw.title) ?? undefined,
      image: pick_string(detail_raw.image) ?? pick_string(detail_raw.thumbnail) ?? undefined,
      thumbnail: pick_string(detail_raw.thumbnail) ?? undefined,
    },
  };
  if (Number.isFinite(catalog_subscription_id) && catalog_subscription_id > 0) {
    (mapped as SubscriptionData & { subscription_id?: number }).subscription_id = catalog_subscription_id;
  }
  (mapped as SubscriptionData & { payment_status?: string }).payment_status =
    pick_string(raw.payment_status) ?? 'pending';
  return mapped;
}
